import React, { useEffect, useState } from 'react';
import { Button, Card } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { downloadAIFile } from './resources';
import { getAIFileMeta } from './resources';

interface FileCardProps {
  openaiId?: string;
}

const FileCard: React.FC<FileCardProps> = ({ openaiId }) => {
  const [meta, setMeta] = useState<any | null>(null);
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      if (!openaiId) return;
      setLoadingMeta(true);
      setErr(null);
      try {
        const res = await getAIFileMeta(openaiId);
        if (!mounted) return;
        // normalize meta object
        const data = res.data || null;
        if (data) {
          // normalize fields; backend may return a signed download URL as `download_url`
          const s3_url = data.s3_url || data.s3Url || data.url || null;
          const download_url = data.download_url || data.downloadUrl || null;
          setMeta({
            filename: data.filename || data.name || data.file_name,
            type: data.type || data.mime || data.content_type,
            openai_file_id: data.openai_file_id || data.openaiId || openaiId,
            s3_url,
            download_url,
            ...data,
          });
        } else {
          setMeta(null);
        }
      } catch (e) {
        if (!mounted) return;
        setErr('Failed to load file metadata');
      } finally {
        if (mounted) setLoadingMeta(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, [openaiId]);

  if (!openaiId) return <div style={{ marginTop: 8 }}>[missing file id]</div>;

  // Prefer a signed download URL when available; otherwise fall back to raw s3_url
  const signedUrl = meta?.download_url || null;
  const s3Url = meta?.s3_url || null;
  const previewUrl = signedUrl || s3Url;

  const handleDownload = async () => {
    setDownloading(true);
    setErr(null);
    try {
      if (signedUrl || s3Url) {
        const href = signedUrl || s3Url;
        const a = document.createElement('a');
        a.href = href;
        a.target = '_blank';
        a.download = meta?.filename || '';
        document.body.appendChild(a);
        a.click();
        a.remove();
        setDownloading(false);
        return;
      }

      // fallback: proxy download via backend which streams from OpenAI
      const res = await downloadAIFile(openaiId);
      // axios may return response.data as an ArrayBuffer or Blob depending on config
      const blob = res.data instanceof Blob ? res.data : new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const fileName = meta?.filename || `file-${openaiId}`;
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setErr('Failed to download file');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Card
      type="inner"
      size="small"
      style={{ marginTop: 8 }}
      title={(
        <div style={{
          display: 'inline-block',
          maxWidth: '80%',
          verticalAlign: 'middle',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          fontSize: 13,
          color: '#333'
        }} title={meta?.filename || undefined}>
          {loadingMeta ? 'Loading file...' : (meta?.filename || 'File')}
        </div>
      )}
      extra={(
        <Button size="small" icon={<DownloadOutlined />} aria-label="Download file" onClick={handleDownload} loading={downloading} />
      )}
    >
      <div style={{ fontSize: 12, color: '#666' }}>{meta?.type ? `${meta.type} • ${meta.openai_file_id || ''}` : (err ? err : '')}</div>
      {/* If this is an image and we have a preview URL (prefer signed), show a preview */}
      {previewUrl && ((meta.type || '').startsWith?.('image/') || /\.(jpg|jpeg|png|gif|webp)(\?|$)/i.test(previewUrl)) ? (
        <div style={{ marginTop: 8 }}>
          <img src={previewUrl} alt={meta.filename || 'file'} style={{ maxWidth: '100%', borderRadius: 6 }} />
        </div>
      ) : null}
    </Card>
  );
};

export default FileCard;
